import React, { useState, useEffect } from "react";

const MockTestSubjectLinkForm = ({ mockTest, subjects, subjectContent, onSubmit, onClose }) => {
  const [linkedSubjects, setLinkedSubjects] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (mockTest && subjects) {
      // Check reverse relationship: find subjects that are in mockTest's subjects array
      const currentlyLinked = subjects
        .filter(s => 
          (mockTest.subjects || []).some(subjId => (subjId._id || subjId) === s._id)
        )
        .map(subject => ({
          subjectId: subject._id,
          subject: subject,
          isLinked: true
        }));
      setLinkedSubjects(currentlyLinked);
    }
  }, [mockTest, subjects]);

  const subjectsWithContent = subjects.filter(subject => {
    const content = subjectContent[subject._id];
    // Show subjects that have at least one mock test PDF (isMockTest = true)
    const hasMockTestPdfs = (content?.mockTests?.length || 0) > 0;
    // Also show subjects that are currently linked (to allow unlinking)
    const isCurrentlyLinked = linkedSubjects.some(ls => ls.subjectId === subject._id);
    return hasMockTestPdfs || isCurrentlyLinked;
  });

  const filteredSubjects = subjectsWithContent.filter(subject =>
    subject.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    subject.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const toggleSubjectLink = (subject) => {
    setLinkedSubjects(prev => {
      const existing = prev.find(s => s.subjectId === subject._id);
      if (existing) {
        return prev.filter(s => s.subjectId !== subject._id);
      }
      return [...prev, { subjectId: subject._id, subject, isLinked: false }];
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const result = await onSubmit(mockTest._id, linkedSubjects);
      if (result?.success) onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content-form bg-white rounded-16 shadow-5" style={{ maxWidth: '900px', maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <div className="modal-header">
          <div>
            <h4 className="modal-title">Link Subjects to {mockTest?.title}</h4>
            <p className="text-13 text-light-1 mt-5">
              Select subjects to include in this mock test package. Mock test PDFs are uploaded per subject in the Subjects tab.
            </p>
          </div>
          <button onClick={onClose} className="modal-close-btn">
            <i className="icon-close"></i>
          </button>
        </div>
        <div className="modal-body" style={{ overflow: 'auto', flex: 1 }}>
          <form onSubmit={handleSubmit} className="content-form">
            <div className="form-group mb-20">
              <input
                type="text"
                placeholder="Search subjects..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="form-input"
                style={{ padding: '10px 15px' }}
              />
            </div>
            <div className="subjects-link-list" style={{ maxHeight: '400px', overflow: 'auto' }}>
              {filteredSubjects.length === 0 ? (
                <div className="text-center py-30 text-13 text-light-2">
                  <i className="icon-search text-30 mb-10"></i>
                  <p>{searchTerm ? 'No subjects found matching your search' : 'No subjects available yet'}</p>
                </div>
              ) : (
                filteredSubjects.map((subject) => {
                  const linkedItem = linkedSubjects.find(s => s.subjectId === subject._id);
                  const isLinked = !!linkedItem;
                  return (
                    <div key={subject._id} className="subject-link-item bg-light-5 rounded-8 p-15 mb-15 border border-light-3">
                      <div className="d-flex items-center justify-between">
                        <label className="checkbox-label" style={{ margin: 0, cursor: 'pointer', flex: 1 }}>
                          <input type="checkbox" checked={isLinked} onChange={() => toggleSubjectLink(subject)} className="form-checkbox mr-10" />
                          <div className="d-flex items-center">
                            <div className="size-35 d-flex justify-center items-center rounded-6 mr-10" style={{ backgroundColor: (subject.color || '#6366f1') + '20', color: subject.color || '#6366f1' }}>
                              <i className={`text-14 ${subject.icon || 'icon-book'}`}></i>
                            </div>
                            <div>
                              <div className="text-14 fw-500">{subject.title}</div>
                              <div className="text-12 text-light-2">{subject.description || 'No description'}</div>
                            </div>
                          </div>
                        </label>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
            <div className="modal-footer mt-20">
              <button type="button" onClick={onClose} className="btn-cancel">Cancel</button>
              <button type="submit" disabled={submitting || linkedSubjects.length === 0} className="btn-primary bg-purple">
                {submitting ? 'Saving...' : `Link ${linkedSubjects.length} Subject${linkedSubjects.length !== 1 ? 's' : ''}`}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default MockTestSubjectLinkForm;
